import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from '@mui/material';
import { useNavigate } from "react-router";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import { FilterSelect, StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getSalaryComponents } from "../../../../../../services/payroll";
import { getActiveLegalUnitId } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";

const BUCKET_TONE = { EARNING: "success", DEDUCTION: "danger", REIMBURSEMENT: "info" };
const calcLabel = (c) =>
  c.calculationType === "PERCENTAGE" ? `${c.value || 0}% of ${c.percentageOf || "CTC"}` : `₹ ${Number(c.value || 0).toLocaleString("en-IN")}/yr`;

const SalaryComponents = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [bucket, setBucket] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const legalUnit = getActiveLegalUnitId();
      const res = await getSalaryComponents(legalUnit);
      if (res?.success === false) throw new Error(res?.message || "Failed to load components");
      setData((Array.isArray(res?.data) ? res.data : []).filter((c) => !c.isDeleted));
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load salary components");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(
    () => (bucket === "all" ? data : data.filter((c) => c.component === bucket)),
    [data, bucket]
  );

  return (
    <MainBox sx={{ padding: "1.25rem" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Salary Components</Box>
          <FilterSelect value={bucket} onChange={(e) => setBucket(e.target.value)}
            options={[{ value: "all", label: "All buckets" }, { value: "EARNING", label: "Earnings" }, { value: "DEDUCTION", label: "Deductions" }, { value: "REIMBURSEMENT", label: "Reimbursements" }]} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <BtnSecondary onClick={() => navigate("/app/settings/payroll/templates")} sx={{ borderRadius: "8px" }}>Templates</BtnSecondary>
          <BtnPrimary onClick={() => navigate("/app/settings/payroll/components/new")} sx={{ borderRadius: "8px" }}>+ Add Component</BtnPrimary>
        </Stack>
      </Stack>

      <DataTable
        rows={filtered}
        loading={loading}
        error={error}
        onRetry={fetchData}
        errorTitle="Couldn't load components"
        empty={{ title: "No components", description: "Add reusable earning, deduction or reimbursement components." }}
        onRowClick={(c) => navigate(`/app/settings/payroll/components/new`, { state: { component: c } })}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          { key: "name", label: "Name", width: "30%", cellSx: { fontWeight: 600 }, render: (c) => c.name },
          {
            key: "bucket",
            label: "Bucket",
            render: (c) => <StatusChip label={(c.component || "").toLowerCase()} tone={BUCKET_TONE[c.component]} />,
          },
          { key: "calc", label: "Calculation", cellSx: { color: tokens.textMuted }, render: (c) => calcLabel(c) },
          {
            key: "status",
            label: "Status",
            render: (c) => <StatusChip label={c.isActive ? "Active" : "Inactive"} tone={c.isActive ? "success" : "muted"} />,
          },
        ]}
      />
    </MainBox>
  );
};

export default SalaryComponents;
